// src/pages/UserManagement.tsx
import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import {
  Plus,
  Download,
  Edit,
  Trash2,
  Shield,
  RefreshCw,
  Users,
  UserCheck,
  UserX,
  Clock,
  AlertTriangle,
  Building2,
} from "lucide-react";
import { userService } from "../services/users";
import { getBranches } from "../services/branches";
import { uploadService } from "../services/uploadService";
import Pagination from "../components/common/Pagination";
import {
  User,
  UserFilters,
  UserRole,
  UserStatus,
  CreateUserRequest,
  UpdateUserRequest,
  UpdateUserRolesRequest,
  UpdateUserStatusRequest,
} from "../types/user";
import { Branch } from "../types/branch";
import { BranchAssignment } from "../components/admin/BranchAssignment";
import { useHorizontalDragScroll } from "../hooks/useHorizontalDragScroll";

const UserManagement: React.FC = () => {
  const tableScrollRef = useHorizontalDragScroll<HTMLDivElement>();
  const [users, setUsers] = useState<User[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

  // Pagination and filtering state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrev: false,
  });

  const [filters, setFilters] = useState<UserFilters>({
    page: 1,
    limit: 10,
    search: "",
    sortBy: "createdAt",
    sortOrder: "desc",
  });

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showBranchModal, setShowBranchModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Profile picture upload state
  const [profilePictureFile, setProfilePictureFile] = useState<File | null>(
    null
  );
  const [uploadingPicture, setUploadingPicture] = useState(false);

  // Form states
  const [createForm, setCreateForm] = useState<CreateUserRequest>({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    roles: [],
    branchId: "",
    status: "pending",
    profileDetails: {
      phone: "",
      dateOfBirth: "",
      gender: undefined,
      address: {
        street: "",
        city: "",
        state: "",
        zipCode: "",
        country: "Kenya",
      },
      designation: "",
    },
  });

  const [editForm, setEditForm] = useState<UpdateUserRequest>({});
  const [roleForm, setRoleForm] = useState<UpdateUserRolesRequest>({
    roles: [],
    reason: "",
  });
  const [statusForm, setStatusForm] = useState<UpdateUserStatusRequest>({
    status: "active",
    reason: "",
  });

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    pending: 0,
    suspended: 0,
  });

  // Load data
  useEffect(() => {
    loadUsers();
    loadBranches();
  }, [filters]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.getUsers(filters);
      setUsers(response.data);
      setPagination(response.pagination);

      // Calculate stats
      const stats = response.data.reduce(
        (acc, user) => {
          acc.total++;
          switch (user.status) {
            case "active":
              acc.active++;
              break;
            case "inactive":
              acc.inactive++;
              break;
            case "pending":
              acc.pending++;
              break;
            case "suspended":
              acc.suspended++;
              break;
          }
          return acc;
        },
        { total: 0, active: 0, inactive: 0, pending: 0, suspended: 0 }
      );
      setStats(stats);
    } catch (err: any) {
      const errorMessage = err.message || "Failed to load users";
      toast.error(errorMessage);
      console.error("Error loading users:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadBranches = async () => {
    try {
      const response = await getBranches();
      setBranches(response.data);
    } catch (err) {
      console.error("Error loading branches:", err);
    }
  };

  // Handler functions
  const handleSearch = (searchTerm: string) => {
    setFilters((prev) => ({ ...prev, search: searchTerm, page: 1 }));
  };

  const handleFilterChange = (key: keyof UserFilters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleSort = (sortBy: string) => {
    setFilters((prev) => ({
      ...prev,
      sortBy,
      sortOrder:
        prev.sortBy === sortBy && prev.sortOrder === "asc" ? "desc" : "asc",
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (size: number) => {
    setFilters((prev) => ({ ...prev, limit: size, page: 1 }));
  };

  const handleCreateUser = async () => {
    try {
      let profilePictureUrl = "";

      // Upload profile picture if provided
      if (profilePictureFile) {
        setUploadingPicture(true);
        try {
          const uploadResult = await uploadService.uploadFile(
            profilePictureFile,
            "profiles"
          );
          if (uploadResult.success && uploadResult.data) {
            profilePictureUrl = uploadResult.data.url;
          } else {
            throw new Error(
              uploadResult.message || "Failed to upload profile picture"
            );
          }
        } catch (uploadError: any) {
          toast.error(
            "Failed to upload profile picture: " + uploadError.message
          );
          setUploadingPicture(false);
          return;
        }
        setUploadingPicture(false);
      }

      // Create user with profile picture URL
      const userData = {
        ...createForm,
        profileDetails: {
          ...createForm.profileDetails,
          profilePicture: profilePictureUrl || undefined,
        },
      };

      await userService.createUser(userData);
      setShowCreateModal(false);
      setCreateForm({
        email: "",
        password: "",
        firstName: "",
        lastName: "",
        roles: [],
        branchId: "",
        status: "pending",
        profileDetails: {
          phone: "",
          dateOfBirth: "",
          gender: undefined,
          address: {
            street: "",
            city: "",
            state: "",
            zipCode: "",
            country: "Kenya",
          },
          designation: "",
        },
      });
      setProfilePictureFile(null);
      setProfilePictureFile(null);
      toast.success("User created successfully");
      loadUsers();
    } catch (err: any) {
      const errorMessage = err.message || "Failed to create user";
      toast.error(errorMessage);
      console.error("Error creating user:", err);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    try {
      let profilePictureUrl = editForm.profileDetails?.profilePicture;

      // Handle profile picture upload
      if (profilePictureFile) {
        setUploadingPicture(true);
        try {
          // Delete existing profile picture if it exists
          if (selectedUser.profileDetails?.profilePicture) {
            // Extract key from URL - assuming URL format is https://domain.com/profiles/filename
            const urlParts =
              selectedUser.profileDetails.profilePicture.split("/");
            const filename = urlParts[urlParts.length - 1];
            const key = `profiles/${filename}`;

            try {
              await uploadService.deleteFile(key);
            } catch (deleteError) {
              console.warn(
                "Failed to delete old profile picture:",
                deleteError
              );
              // Don't fail the update if delete fails
            }
          }

          // Upload new profile picture
          const uploadResult = await uploadService.uploadFile(
            profilePictureFile,
            "profiles"
          );
          if (uploadResult.success && uploadResult.data) {
            profilePictureUrl = uploadResult.data.url;
          } else {
            throw new Error(
              uploadResult.message || "Failed to upload profile picture"
            );
          }
        } catch (uploadError: any) {
          toast.error(
            "Failed to upload profile picture: " + uploadError.message
          );
          setUploadingPicture(false);
          return;
        } finally {
          setUploadingPicture(false);
        }
      }

      // Update user with new profile picture URL
      const updateData = {
        ...editForm,
        profileDetails: {
          ...editForm.profileDetails,
          profilePicture: profilePictureUrl,
        },
      };

      await userService.updateUser(selectedUser._id, updateData);
      setShowEditModal(false);
      setEditForm({});
      setSelectedUser(null);
      setProfilePictureFile(null);
      toast.success("User updated successfully");
      loadUsers();
    } catch (err: any) {
      const errorMessage = err.message || "Failed to update user";
      toast.error(errorMessage);
      console.error("Error updating user:", err);
    }
  };

  const handleUpdateRoles = async () => {
    if (!selectedUser) return;

    try {
      await userService.updateUserRoles(selectedUser._id, roleForm);
      setShowRoleModal(false);
      setRoleForm({ roles: [], reason: "" });
      setSelectedUser(null);
      toast.success("User roles updated successfully");
      loadUsers();
    } catch (err: any) {
      const errorMessage = err.message || "Failed to update user roles";
      toast.error(errorMessage);
      console.error("Error updating roles:", err);
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedUser) return;

    try {
      await userService.updateUserStatus(selectedUser._id, statusForm);
      setShowStatusModal(false);
      setStatusForm({ status: "active", reason: "" });
      setSelectedUser(null);
      toast.success("User status updated successfully");
      loadUsers();
    } catch (err: any) {
      const errorMessage = err.message || "Failed to update user status";
      toast.error(errorMessage);
      console.error("Error updating status:", err);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      await userService.deleteUser(selectedUser._id);
      setShowDeleteModal(false);
      setSelectedUser(null);
      toast.success("User deleted successfully");
      loadUsers();
    } catch (err: any) {
      const errorMessage = err.message || "Failed to delete user";
      toast.error(errorMessage);
      console.error("Error deleting user:", err);
    }
  };

  const handleExportUsers = async () => {
    try {
      const blob = await userService.exportUsers(filters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `users-${new Date().toISOString().split("T")[0]}.csv`;
      
      try {
        document.body.appendChild(a);
        a.click();
      } finally {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
      toast.success("Users exported successfully");
    } catch (err: any) {
      const errorMessage = err.message || "Failed to export users";
      toast.error(errorMessage);
      console.error("Error exporting users:", err);
    }
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setEditForm({
      firstName: user.firstName,
      lastName: user.lastName,
      profileDetails: user.profileDetails,
    });
    setShowEditModal(true);
  };

  const openRoleModal = (user: User) => {
    setSelectedUser(user);
    setRoleForm({
      roles: [...user.roles],
      reason: "",
    });
    setShowRoleModal(true);
  };

  const openStatusModal = (user: User) => {
    setSelectedUser(user);
    setStatusForm({
      status: user.status,
      reason: "",
    });
    setShowStatusModal(true);
  };

  const openDeleteModal = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const openDetailsModal = (user: User) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
  };

  const getStatusColor = (status: UserStatus) => {
    switch (status) {
      case "active":
        return "bg-gradient-to-r from-emerald-300 to-green-400 text-white";
      case "inactive":
        return "bg-gradient-to-r from-slate-300 to-slate-400 text-white";
      case "pending":
        return "bg-gradient-to-r from-amber-300 to-orange-400 text-white";
      case "suspended":
        return "bg-gradient-to-r from-red-300 to-red-400 text-white";
      default:
        return "bg-gradient-to-r from-slate-300 to-slate-400 text-white";
    }
  };

  const getRoleColor = (role: UserRole) => {
    switch (role) {
      case "superadmin":
        return "bg-gradient-to-r from-purple-300 to-purple-400 text-white";
      case "admin":
        return "bg-gradient-to-r from-blue-300 to-indigo-400 text-white";
      case "teacher":
        return "bg-gradient-to-r from-emerald-300 to-green-400 text-white";
      case "secretary":
        return "bg-gradient-to-r from-amber-300 to-orange-400 text-white";
      case "student":
        return "bg-gradient-to-r from-cyan-300 to-cyan-400 text-white";
      default:
        return "bg-gradient-to-r from-slate-300 to-slate-400 text-white";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-4 shadow-lg">
            <Users className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">
              User Management
            </h1>
            <p className="text-slate-600 font-semibold">
              Manage users, roles, and permissions
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-white border-2 border-slate-200 rounded-xl px-6 py-3 font-bold text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-3 shadow-md"
        >
          <Plus className="w-5 h-5 text-blue-500" />
          Add User
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">Total Users</p>
              <p className="text-3xl font-bold text-slate-900 mt-2">
                {stats.total}
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-4 shadow-lg">
              <Users className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">Active</p>
              <p className="text-3xl font-bold text-emerald-600 mt-2">
                {stats.active}
              </p>
            </div>
            <div className="bg-gradient-to-br from-emerald-300 to-green-400 rounded-2xl p-4 shadow-lg">
              <UserCheck className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">Inactive</p>
              <p className="text-3xl font-bold text-slate-600 mt-2">
                {stats.inactive}
              </p>
            </div>
            <div className="bg-gradient-to-br from-slate-300 to-slate-400 rounded-2xl p-4 shadow-lg">
              <UserX className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">Pending</p>
              <p className="text-3xl font-bold text-amber-600 mt-2">
                {stats.pending}
              </p>
            </div>
            <div className="bg-gradient-to-br from-amber-300 to-orange-400 rounded-2xl p-4 shadow-lg">
              <Clock className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-sm font-bold text-slate-900">Suspended</p>
              <p className="text-3xl font-bold text-red-600 mt-2">
                {stats.suspended}
              </p>
            </div>
            <div className="bg-gradient-to-br from-red-300 to-red-400 rounded-2xl p-4 shadow-lg">
              <AlertTriangle className="h-8 w-8 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <input
              type="text"
              placeholder="Search users..."
              value={filters.search || ""}
              onChange={(e) => handleSearch(e.target.value)}
              className="px-4 py-3 w-full bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
            />
          </div>

          {/* Status Filter */}
          <select
            value={filters.status || ""}
            onChange={(e) =>
              handleFilterChange("status", e.target.value || undefined)
            }
            className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="pending">Pending</option>
            <option value="suspended">Suspended</option>
          </select>

          {/* Role Filter */}
          <select
            value={filters.role || ""}
            onChange={(e) =>
              handleFilterChange("role", e.target.value || undefined)
            }
            className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
          >
            <option value="">All Roles</option>
            <option value="superadmin">Super Admin</option>
            <option value="admin">Admin</option>
            <option value="teacher">Teacher</option>
            <option value="secretary">Secretary</option>
            <option value="student">Student</option>
          </select>

          {/* Branch Filter */}
          <select
            value={filters.branchId || ""}
            onChange={(e) =>
              handleFilterChange("branchId", e.target.value || undefined)
            }
            className="px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
          >
            <option value="">All Branches</option>
            {branches.map((branch) => (
              <option key={branch._id} value={branch._id}>
                {branch.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mt-6 pt-4 border-t border-slate-200">
          <div className="flex items-center gap-3">
            <button
              onClick={loadUsers}
              disabled={loading}
              className="bg-white border-2 border-slate-200 rounded-xl px-4 py-2.5 font-bold text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
            >
              <RefreshCw
                className={`w-5 h-5 text-blue-500 ${
                  loading ? "animate-spin" : ""
                }`}
              />
              Refresh
            </button>
            <button
              onClick={handleExportUsers}
              className="bg-white border-2 border-slate-200 rounded-xl px-4 py-2.5 font-bold text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
            >
              <Download className="w-5 h-5 text-emerald-500" />
              Export
            </button>
          </div>

          <div className="flex items-center">
            <span className="text-sm text-slate-600 font-semibold">
              Showing {((filters.page || 1) - 1) * (filters.limit || 10) + 1} to{" "}
              {Math.min(
                (filters.page || 1) * (filters.limit || 10),
                pagination.totalItems
              )}{" "}
              of {pagination.totalItems} users
            </span>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl overflow-hidden">
        <div ref={tableScrollRef} className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-gradient-to-r from-slate-50 to-blue-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={
                      selectedUsers.length === users.length && users.length > 0
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedUsers(users.map((user) => user._id));
                      } else {
                        setSelectedUsers([]);
                      }
                    }}
                    className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                  />
                </th>
                <th
                  className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/50 transition-colors"
                  onClick={() => handleSort("firstName")}
                >
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-500" />
                    User
                  </div>
                </th>
                <th
                  className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/50 transition-colors"
                  onClick={() => handleSort("email")}
                >
                  Email
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-purple-500" />
                    Roles
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Branch
                </th>
                <th
                  className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/50 transition-colors"
                  onClick={() => handleSort("status")}
                >
                  Status
                </th>
                <th
                  className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider cursor-pointer hover:bg-blue-100/50 transition-colors"
                  onClick={() => handleSort("lastLogin")}
                >
                  Last Login
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-200">
              {users.map((user) => (
                <tr
                  key={user._id}
                  className="hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50 cursor-pointer transition-all"
                  onClick={() => openDetailsModal(user)}
                >
                  <td
                    className="px-6 py-4 whitespace-nowrap"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user._id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUsers((prev) => [...prev, user._id]);
                        } else {
                          setSelectedUsers((prev) =>
                            prev.filter((id) => id !== user._id)
                          );
                        }
                      }}
                      className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        {user.profileDetails?.profilePicture ? (
                          <img
                            src={user.profileDetails.profilePicture}
                            alt={`${user.firstName} ${user.lastName}`}
                            className="h-10 w-10 rounded-2xl object-cover shadow-md"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display =
                                "none";
                              (
                                e.target as HTMLImageElement
                              ).nextElementSibling?.classList.remove("hidden");
                            }}
                          />
                        ) : null}
                        <div
                          className={`h-10 w-10 rounded-2xl bg-gradient-to-br from-blue-300 to-indigo-400 flex items-center justify-center text-sm font-bold text-white shadow-md ${
                            user.profileDetails?.profilePicture ? "hidden" : ""
                          }`}
                        >
                          {user.firstName[0]}
                          {user.lastName[0]}
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-bold text-slate-900">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-xs text-slate-600 font-semibold">
                          ID: {user._id.slice(-6)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-semibold text-slate-900">
                      {user.email}
                    </div>
                    {user.emailVerified ? (
                      <div className="text-xs font-bold text-emerald-600">
                        Verified
                      </div>
                    ) : (
                      <div className="text-xs font-bold text-red-600">
                        Unverified
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {user.roles.map((role) => (
                        <span
                          key={role}
                          className={`inline-flex items-center px-3 py-1.5 rounded-xl text-xs font-bold shadow-md ${getRoleColor(
                            role
                          )}`}
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-semibold text-slate-900">
                      {user.branchInfo?.name || "No Branch"}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-3 py-1.5 rounded-xl text-xs font-bold shadow-md ${getStatusColor(
                        user.status
                      )}`}
                    >
                      {user.status}
                    </span>
                    {user.isLocked && (
                      <div className="text-xs font-bold text-red-600 mt-1">
                        Locked
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-600">
                    {user.lastLogin
                      ? new Date(user.lastLogin).toLocaleDateString()
                      : "Never"}
                  </td>
                  <td
                    className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => openEditModal(user)}
                        className="bg-white border-2 border-slate-200 rounded-xl p-2 hover:shadow-lg hover:scale-105 transition-all duration-300"
                        title="Edit User"
                      >
                        <Edit className="w-4 h-4 text-blue-500" />
                      </button>
                      <button
                        onClick={() => openRoleModal(user)}
                        className="bg-white border-2 border-slate-200 rounded-xl p-2 hover:shadow-lg hover:scale-105 transition-all duration-300"
                        title="Manage Roles"
                      >
                        <Shield className="w-4 h-4 text-emerald-500" />
                      </button>
                      {(user.roles.includes("admin") ||
                        user.roles.includes("branchadmin")) && (
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowBranchModal(true);
                          }}
                          className="bg-white border-2 border-slate-200 rounded-xl p-2 hover:shadow-lg hover:scale-105 transition-all duration-300"
                          title="Manage Branches"
                        >
                          <Building2 className="w-4 h-4 text-purple-500" />
                        </button>
                      )}
                      <button
                        onClick={() => openStatusModal(user)}
                        className="bg-white border-2 border-slate-200 rounded-xl p-2 hover:shadow-lg hover:scale-105 transition-all duration-300"
                        title="Change Status"
                      >
                        <RefreshCw className="w-4 h-4 text-amber-500" />
                      </button>
                      <button
                        onClick={() => openDeleteModal(user)}
                        className="bg-white border-2 border-slate-200 rounded-xl p-2 hover:shadow-lg hover:scale-105 transition-all duration-300"
                        title="Delete User"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
            pageSize={filters.limit}
            onPageSizeChange={handlePageSizeChange}
            totalItems={pagination.totalItems}
            showPageSizeSelector={true}
          />
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600/50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                Create New User
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={createForm.firstName}
                      onChange={(e) =>
                        setCreateForm((prev) => ({
                          ...prev,
                          firstName: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={createForm.lastName}
                      onChange={(e) =>
                        setCreateForm((prev) => ({
                          ...prev,
                          lastName: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={createForm.email}
                    onChange={(e) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password *
                  </label>
                  <input
                    type="password"
                    value={createForm.password}
                    onChange={(e) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        password: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    required
                    minLength={6}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Roles *
                  </label>
                  <div className="space-y-2">
                    {[
                      "superadmin",
                      "admin",
                      "teacher",
                      "secretary",
                      "student",
                    ].map((role) => (
                      <label key={role} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={createForm.roles.includes(role as UserRole)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCreateForm((prev) => ({
                                ...prev,
                                roles: [...prev.roles, role as UserRole],
                              }));
                            } else {
                              setCreateForm((prev) => ({
                                ...prev,
                                roles: prev.roles.filter((r) => r !== role),
                              }));
                            }
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm capitalize">{role}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {createForm.roles.some((role) => role !== "superadmin") && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Branch *
                    </label>
                    <select
                      value={createForm.branchId}
                      onChange={(e) =>
                        setCreateForm((prev) => ({
                          ...prev,
                          branchId: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      required
                    >
                      <option value="">Select a branch</option>
                      {branches.map((branch) => (
                        <option key={branch._id} value={branch._id}>
                          {branch.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Initial Status
                  </label>
                  <select
                    value={createForm.status}
                    onChange={(e) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        status: e.target.value as UserStatus,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="pending">Pending</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>

                {/* Additional fields for admin and secretary */}
                {(createForm.roles.includes("admin") ||
                  createForm.roles.includes("secretary")) && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          value={createForm.profileDetails?.phone || ""}
                          onChange={(e) =>
                            setCreateForm((prev) => ({
                              ...prev,
                              profileDetails: {
                                ...prev.profileDetails,
                                phone: e.target.value,
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="+254 XXX XXX XXX"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Date of Birth
                        </label>
                        <input
                          type="date"
                          value={createForm.profileDetails?.dateOfBirth || ""}
                          onChange={(e) =>
                            setCreateForm((prev) => ({
                              ...prev,
                              profileDetails: {
                                ...prev.profileDetails,
                                dateOfBirth: e.target.value,
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Gender
                        </label>
                        <select
                          value={createForm.profileDetails?.gender || ""}
                          onChange={(e) =>
                            setCreateForm((prev) => ({
                              ...prev,
                              profileDetails: {
                                ...prev.profileDetails,
                                gender: e.target.value as any,
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Select gender</option>
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Designation
                        </label>
                        <input
                          type="text"
                          value={createForm.profileDetails?.designation || ""}
                          onChange={(e) =>
                            setCreateForm((prev) => ({
                              ...prev,
                              profileDetails: {
                                ...prev.profileDetails,
                                designation: e.target.value,
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="e.g. Branch Manager, Secretary"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <input
                          type="text"
                          value={
                            createForm.profileDetails?.address?.street || ""
                          }
                          onChange={(e) =>
                            setCreateForm((prev) => ({
                              ...prev,
                              profileDetails: {
                                ...prev.profileDetails,
                                address: {
                                  ...prev.profileDetails?.address,
                                  street: e.target.value,
                                },
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Street Address"
                        />
                        <input
                          type="text"
                          value={createForm.profileDetails?.address?.city || ""}
                          onChange={(e) =>
                            setCreateForm((prev) => ({
                              ...prev,
                              profileDetails: {
                                ...prev.profileDetails,
                                address: {
                                  ...prev.profileDetails?.address,
                                  city: e.target.value,
                                },
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="City"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-4 mt-2">
                        <input
                          type="text"
                          value={
                            createForm.profileDetails?.address?.state || ""
                          }
                          onChange={(e) =>
                            setCreateForm((prev) => ({
                              ...prev,
                              profileDetails: {
                                ...prev.profileDetails,
                                address: {
                                  ...prev.profileDetails?.address,
                                  state: e.target.value,
                                },
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="State/Province"
                        />
                        <input
                          type="text"
                          value={
                            createForm.profileDetails?.address?.zipCode || ""
                          }
                          onChange={(e) =>
                            setCreateForm((prev) => ({
                              ...prev,
                              profileDetails: {
                                ...prev.profileDetails,
                                address: {
                                  ...prev.profileDetails?.address,
                                  zipCode: e.target.value,
                                },
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="ZIP/Postal Code"
                        />
                        <input
                          type="text"
                          value={
                            createForm.profileDetails?.address?.country ||
                            "Kenya"
                          }
                          onChange={(e) =>
                            setCreateForm((prev) => ({
                              ...prev,
                              profileDetails: {
                                ...prev.profileDetails,
                                address: {
                                  ...prev.profileDetails?.address,
                                  country: e.target.value,
                                },
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Country"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Profile Picture
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setProfilePictureFile(file);
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                      {profilePictureFile && (
                        <p className="text-sm text-gray-600 mt-1">
                          Selected: {profilePictureFile.name}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Emergency Contact
                      </label>
                      <div className="grid grid-cols-3 gap-4">
                        <input
                          type="text"
                          value={
                            createForm.profileDetails?.emergencyContact?.name ||
                            ""
                          }
                          onChange={(e) =>
                            setCreateForm((prev) => ({
                              ...prev,
                              profileDetails: {
                                ...prev.profileDetails,
                                emergencyContact: {
                                  ...prev.profileDetails?.emergencyContact,
                                  name: e.target.value,
                                },
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Contact Name"
                        />
                        <input
                          type="tel"
                          value={
                            createForm.profileDetails?.emergencyContact
                              ?.phone || ""
                          }
                          onChange={(e) =>
                            setCreateForm((prev) => ({
                              ...prev,
                              profileDetails: {
                                ...prev.profileDetails,
                                emergencyContact: {
                                  ...prev.profileDetails?.emergencyContact,
                                  phone: e.target.value,
                                },
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Contact Phone"
                        />
                        <input
                          type="text"
                          value={
                            createForm.profileDetails?.emergencyContact
                              ?.relationship || ""
                          }
                          onChange={(e) =>
                            setCreateForm((prev) => ({
                              ...prev,
                              profileDetails: {
                                ...prev.profileDetails,
                                emergencyContact: {
                                  ...prev.profileDetails?.emergencyContact,
                                  relationship: e.target.value,
                                },
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Relationship"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Salary Information (Optional)
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <input
                          type="number"
                          value={
                            createForm.profileDetails?.salaryDetails
                              ?.basicSalary || ""
                          }
                          onChange={(e) =>
                            setCreateForm((prev) => ({
                              ...prev,
                              profileDetails: {
                                ...prev.profileDetails,
                                salaryDetails: {
                                  ...prev.profileDetails?.salaryDetails,
                                  basicSalary: e.target.value
                                    ? parseFloat(e.target.value)
                                    : undefined,
                                },
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Basic Salary"
                          min="0"
                          step="0.01"
                        />
                        <input
                          type="text"
                          value={
                            createForm.profileDetails?.salaryDetails
                              ?.bankName || ""
                          }
                          onChange={(e) =>
                            setCreateForm((prev) => ({
                              ...prev,
                              profileDetails: {
                                ...prev.profileDetails,
                                salaryDetails: {
                                  ...prev.profileDetails?.salaryDetails,
                                  bankName: e.target.value,
                                },
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Bank Name"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-4 mt-2">
                        <input
                          type="text"
                          value={
                            createForm.profileDetails?.salaryDetails
                              ?.accountNumber || ""
                          }
                          onChange={(e) =>
                            setCreateForm((prev) => ({
                              ...prev,
                              profileDetails: {
                                ...prev.profileDetails,
                                salaryDetails: {
                                  ...prev.profileDetails?.salaryDetails,
                                  accountNumber: e.target.value,
                                },
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Account Number"
                        />
                        <input
                          type="text"
                          value={
                            createForm.profileDetails?.salaryDetails
                              ?.accountName || ""
                          }
                          onChange={(e) =>
                            setCreateForm((prev) => ({
                              ...prev,
                              profileDetails: {
                                ...prev.profileDetails,
                                salaryDetails: {
                                  ...prev.profileDetails?.salaryDetails,
                                  accountName: e.target.value,
                                },
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Account Name"
                        />
                        <input
                          type="text"
                          value={
                            createForm.profileDetails?.salaryDetails
                              ?.paymentMethod || ""
                          }
                          onChange={(e) =>
                            setCreateForm((prev) => ({
                              ...prev,
                              profileDetails: {
                                ...prev.profileDetails,
                                salaryDetails: {
                                  ...prev.profileDetails?.salaryDetails,
                                  paymentMethod: e.target.value,
                                },
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Payment Method"
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateForm({
                      email: "",
                      password: "",
                      firstName: "",
                      lastName: "",
                      roles: [],
                      branchId: "",
                      status: "pending",
                      profileDetails: {
                        phone: "",
                        dateOfBirth: "",
                        gender: undefined,
                        address: {
                          street: "",
                          city: "",
                          state: "",
                          zipCode: "",
                          country: "Kenya",
                        },
                        designation: "",
                        emergencyContact: {
                          name: "",
                          phone: "",
                          relationship: "",
                        },
                        salaryDetails: {
                          basicSalary: undefined,
                          bankName: "",
                          accountNumber: "",
                          accountName: "",
                          paymentMethod: "",
                        },
                      },
                    });
                    setProfilePictureFile(null);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateUser}
                  disabled={
                    !createForm.email ||
                    !createForm.password ||
                    !createForm.firstName ||
                    !createForm.lastName ||
                    createForm.roles.length === 0 ||
                    uploadingPicture
                  }
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {uploadingPicture ? "Uploading..." : "Create User"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Additional modals */}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600/50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                Edit User: {selectedUser.firstName} {selectedUser.lastName}
              </h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name
                    </label>
                    <input
                      type="text"
                      value={editForm.firstName || ""}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          firstName: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name
                    </label>
                    <input
                      type="text"
                      value={editForm.lastName || ""}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          lastName: e.target.value,
                        }))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="text"
                    value={editForm.profileDetails?.phone || ""}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        profileDetails: {
                          ...prev.profileDetails,
                          phone: e.target.value,
                        },
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    value={editForm.profileDetails?.dateOfBirth || ""}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        profileDetails: {
                          ...prev.profileDetails,
                          dateOfBirth: e.target.value,
                        },
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gender
                  </label>
                  <select
                    value={editForm.profileDetails?.gender || ""}
                    onChange={(e) =>
                      setEditForm((prev) => ({
                        ...prev,
                        profileDetails: {
                          ...prev.profileDetails,
                          gender: e.target.value as any,
                        },
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Additional fields for admin and secretary */}
                {(selectedUser?.roles.includes("admin") ||
                  selectedUser?.roles.includes("secretary")) && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Emergency Contact
                      </label>
                      <div className="grid grid-cols-3 gap-4">
                        <input
                          type="text"
                          value={
                            editForm.profileDetails?.emergencyContact?.name ||
                            ""
                          }
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              profileDetails: {
                                ...prev.profileDetails,
                                emergencyContact: {
                                  ...prev.profileDetails?.emergencyContact,
                                  name: e.target.value,
                                },
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Contact Name"
                        />
                        <input
                          type="tel"
                          value={
                            editForm.profileDetails?.emergencyContact?.phone ||
                            ""
                          }
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              profileDetails: {
                                ...prev.profileDetails,
                                emergencyContact: {
                                  ...prev.profileDetails?.emergencyContact,
                                  phone: e.target.value,
                                },
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Contact Phone"
                        />
                        <input
                          type="text"
                          value={
                            editForm.profileDetails?.emergencyContact
                              ?.relationship || ""
                          }
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              profileDetails: {
                                ...prev.profileDetails,
                                emergencyContact: {
                                  ...prev.profileDetails?.emergencyContact,
                                  relationship: e.target.value,
                                },
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Relationship"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Salary Information (Optional)
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <input
                          type="number"
                          value={
                            editForm.profileDetails?.salaryDetails
                              ?.basicSalary || ""
                          }
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              profileDetails: {
                                ...prev.profileDetails,
                                salaryDetails: {
                                  ...prev.profileDetails?.salaryDetails,
                                  basicSalary: e.target.value
                                    ? parseFloat(e.target.value)
                                    : undefined,
                                },
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Basic Salary"
                          min="0"
                          step="0.01"
                        />
                        <input
                          type="text"
                          value={
                            editForm.profileDetails?.salaryDetails?.bankName ||
                            ""
                          }
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              profileDetails: {
                                ...prev.profileDetails,
                                salaryDetails: {
                                  ...prev.profileDetails?.salaryDetails,
                                  bankName: e.target.value,
                                },
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Bank Name"
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-4 mt-2">
                        <input
                          type="text"
                          value={
                            editForm.profileDetails?.salaryDetails
                              ?.accountNumber || ""
                          }
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              profileDetails: {
                                ...prev.profileDetails,
                                salaryDetails: {
                                  ...prev.profileDetails?.salaryDetails,
                                  accountNumber: e.target.value,
                                },
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Account Number"
                        />
                        <input
                          type="text"
                          value={
                            editForm.profileDetails?.salaryDetails
                              ?.accountName || ""
                          }
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              profileDetails: {
                                ...prev.profileDetails,
                                salaryDetails: {
                                  ...prev.profileDetails?.salaryDetails,
                                  accountName: e.target.value,
                                },
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Account Name"
                        />
                        <input
                          type="text"
                          value={
                            editForm.profileDetails?.salaryDetails
                              ?.branchName || ""
                          }
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              profileDetails: {
                                ...prev.profileDetails,
                                salaryDetails: {
                                  ...prev.profileDetails?.salaryDetails,
                                  branchName: e.target.value,
                                },
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Branch Name"
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Profile Picture Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Profile Picture
                  </label>
                  {selectedUser?.profileDetails?.profilePicture && (
                    <div className="mb-2">
                      <img
                        src={selectedUser.profileDetails.profilePicture}
                        alt="Current profile"
                        className="w-20 h-20 object-cover rounded-full border"
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Current profile picture
                      </p>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setProfilePictureFile(file);
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                  {profilePictureFile && (
                    <p className="text-sm text-green-600 mt-1">
                      Selected: {profilePictureFile.name}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditForm({});
                    setSelectedUser(null);
                    setProfilePictureFile(null);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateUser}
                  disabled={uploadingPicture}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadingPicture ? "Uploading..." : "Update User"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Role Management Modal */}
      {showRoleModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600/50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                Manage Roles: {selectedUser.firstName} {selectedUser.lastName}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Roles: {selectedUser.roles.join(", ")}
                  </label>
                  <div className="space-y-2">
                    {[
                      "superadmin",
                      "admin",
                      "teacher",
                      "secretary",
                      "student",
                    ].map((role) => (
                      <label key={role} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={roleForm.roles.includes(role as UserRole)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setRoleForm((prev) => ({
                                ...prev,
                                roles: [...prev.roles, role as UserRole],
                              }));
                            } else {
                              setRoleForm((prev) => ({
                                ...prev,
                                roles: prev.roles.filter((r) => r !== role),
                              }));
                            }
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm capitalize">{role}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for Change
                  </label>
                  <textarea
                    value={roleForm.reason || ""}
                    onChange={(e) =>
                      setRoleForm((prev) => ({
                        ...prev,
                        reason: e.target.value,
                      }))
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Optional reason for role change..."
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <button
                  onClick={() => {
                    setShowRoleModal(false);
                    setRoleForm({ roles: [], reason: "" });
                    setSelectedUser(null);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateRoles}
                  disabled={roleForm.roles.length === 0}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Update Roles
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Management Modal */}
      {showStatusModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600/50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">
                Change Status: {selectedUser.firstName} {selectedUser.lastName}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Status:{" "}
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        selectedUser.status
                      )}`}
                    >
                      {selectedUser.status}
                    </span>
                  </label>
                  <select
                    value={statusForm.status}
                    onChange={(e) =>
                      setStatusForm((prev) => ({
                        ...prev,
                        status: e.target.value as UserStatus,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for Change
                  </label>
                  <textarea
                    value={statusForm.reason || ""}
                    onChange={(e) =>
                      setStatusForm((prev) => ({
                        ...prev,
                        reason: e.target.value,
                      }))
                    }
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="Optional reason for status change..."
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <button
                  onClick={() => {
                    setShowStatusModal(false);
                    setStatusForm({ status: "active", reason: "" });
                    setSelectedUser(null);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateStatus}
                  className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors"
                >
                  Update Status
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600/50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4 text-center">
                Delete User
              </h3>
              <div className="mb-4">
                <p className="text-sm text-gray-500 text-center">
                  Are you sure you want to delete user{" "}
                  <strong>
                    {selectedUser.firstName} {selectedUser.lastName}
                  </strong>
                  ? This action cannot be undone.
                </p>
                <div className="mt-3 bg-gray-50 rounded-lg p-3">
                  <div className="text-sm text-gray-600">
                    <p>
                      <strong>Email:</strong> {selectedUser.email}
                    </p>
                    <p>
                      <strong>Roles:</strong> {selectedUser.roles.join(", ")}
                    </p>
                    <p>
                      <strong>Status:</strong> {selectedUser.status}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedUser(null);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUser}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Delete User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {showDetailsModal && selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-y-auto mx-4">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center space-x-4">
                {selectedUser?.profileDetails?.profilePicture ? (
                  <img
                    src={selectedUser.profileDetails.profilePicture}
                    alt={`${selectedUser.firstName} ${selectedUser.lastName}`}
                    className="h-12 w-12 rounded-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                      (
                        e.target as HTMLImageElement
                      ).nextElementSibling?.classList.remove("hidden");
                    }}
                  />
                ) : null}
                <div
                  className={`h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center text-lg font-medium text-gray-600 ${
                    selectedUser?.profileDetails?.profilePicture ? "hidden" : ""
                  }`}
                >
                  {selectedUser.firstName[0]}
                  {selectedUser.lastName[0]}
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {selectedUser.firstName} {selectedUser.lastName}
                  </h2>
                  <div className="flex items-center space-x-2 mt-1">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        selectedUser.status
                      )}`}
                    >
                      {selectedUser.status}
                    </span>
                    {selectedUser.isLocked && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Locked
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="p-6">
              {/* Quick Actions */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-900 mb-3">
                  Quick Actions
                </h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      openEditModal(selectedUser);
                    }}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <Edit className="w-4 h-4 mr-1" />
                    Edit Profile
                  </button>
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      openRoleModal(selectedUser);
                    }}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <Shield className="w-4 h-4 mr-1" />
                    Manage Roles
                  </button>
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      openStatusModal(selectedUser);
                    }}
                    className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Change Status
                  </button>
                  {selectedUser.roles.includes("student") && (
                    <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                      View Fees
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowDetailsModal(false);
                      openDeleteModal(selectedUser);
                    }}
                    className="inline-flex items-center px-3 py-1.5 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete User
                  </button>
                </div>
              </div>

              {/* User Information Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Basic Information */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Basic Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        User ID
                      </label>
                      <p className="text-sm text-gray-900">
                        {selectedUser._id}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Email
                      </label>
                      <div className="flex items-center space-x-2">
                        <p className="text-sm text-gray-900">
                          {selectedUser.email}
                        </p>
                        {selectedUser.emailVerified ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Verified
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Unverified
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Full Name
                      </label>
                      <p className="text-sm text-gray-900">
                        {selectedUser.firstName} {selectedUser.lastName}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Roles
                      </label>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {selectedUser.roles.map((role) => (
                          <span
                            key={role}
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(
                              role
                            )}`}
                          >
                            {role}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Branch
                      </label>
                      <p className="text-sm text-gray-900">
                        {selectedUser.branchInfo?.name || "No Branch Assigned"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Registration Date
                      </label>
                      <p className="text-sm text-gray-900">
                        {new Date(selectedUser.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Last Updated
                      </label>
                      <p className="text-sm text-gray-900">
                        {new Date(selectedUser.updatedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Profile Details */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Profile Details
                  </h3>
                  <div className="space-y-3">
                    {selectedUser.profileDetails?.dateOfBirth && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Date of Birth
                        </label>
                        <p className="text-sm text-gray-900">
                          {new Date(
                            selectedUser.profileDetails.dateOfBirth
                          ).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    {selectedUser.profileDetails?.gender && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Gender
                        </label>
                        <p className="text-sm text-gray-900 capitalize">
                          {selectedUser.profileDetails.gender}
                        </p>
                      </div>
                    )}
                    {selectedUser.profileDetails?.phone && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Phone
                        </label>
                        <p className="text-sm text-gray-900">
                          {selectedUser.profileDetails.phone}
                        </p>
                      </div>
                    )}
                    {selectedUser.profileDetails?.address && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">
                          Address
                        </label>
                        <p className="text-sm text-gray-900">
                          {[
                            selectedUser.profileDetails.address.street,
                            selectedUser.profileDetails.address.city,
                            selectedUser.profileDetails.address.state,
                            selectedUser.profileDetails.address.zipCode,
                            selectedUser.profileDetails.address.country,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                        </p>
                      </div>
                    )}

                    {/* Student-specific fields */}
                    {selectedUser.roles.includes("student") && (
                      <>
                        {selectedUser.profileDetails?.studentId && (
                          <div>
                            <label className="text-sm font-medium text-gray-500">
                              Student ID
                            </label>
                            <p className="text-sm text-gray-900">
                              {selectedUser.profileDetails.studentId}
                            </p>
                          </div>
                        )}
                        {selectedUser.profileDetails?.admissionDate && (
                          <div>
                            <label className="text-sm font-medium text-gray-500">
                              Admission Date
                            </label>
                            <p className="text-sm text-gray-900">
                              {new Date(
                                selectedUser.profileDetails.admissionDate
                              ).toLocaleDateString()}
                            </p>
                          </div>
                        )}
                        {selectedUser.profileDetails?.guardianName && (
                          <div>
                            <label className="text-sm font-medium text-gray-500">
                              Guardian
                            </label>
                            <p className="text-sm text-gray-900">
                              {selectedUser.profileDetails.guardianName}
                              {selectedUser.profileDetails.guardianPhone &&
                                ` (${selectedUser.profileDetails.guardianPhone})`}
                            </p>
                          </div>
                        )}
                      </>
                    )}

                    {/* Teacher-specific fields */}
                    {selectedUser.roles.includes("teacher") && (
                      <>
                        {selectedUser.profileDetails?.employeeId && (
                          <div>
                            <label className="text-sm font-medium text-gray-500">
                              Employee ID
                            </label>
                            <p className="text-sm text-gray-900">
                              {selectedUser.profileDetails.employeeId}
                            </p>
                          </div>
                        )}
                        {selectedUser.profileDetails?.department && (
                          <div>
                            <label className="text-sm font-medium text-gray-500">
                              Department
                            </label>
                            <p className="text-sm text-gray-900">
                              {selectedUser.profileDetails.department}
                            </p>
                          </div>
                        )}
                        {selectedUser.profileDetails?.subjects &&
                          selectedUser.profileDetails.subjects.length > 0 && (
                            <div>
                              <label className="text-sm font-medium text-gray-500">
                                Subjects
                              </label>
                              <p className="text-sm text-gray-900">
                                {selectedUser.profileDetails.subjects.join(
                                  ", "
                                )}
                              </p>
                            </div>
                          )}
                        {selectedUser.profileDetails?.qualification && (
                          <div>
                            <label className="text-sm font-medium text-gray-500">
                              Qualification
                            </label>
                            <p className="text-sm text-gray-900">
                              {selectedUser.profileDetails.qualification}
                            </p>
                          </div>
                        )}
                        {selectedUser.profileDetails?.experience !==
                          undefined && (
                          <div>
                            <label className="text-sm font-medium text-gray-500">
                              Experience
                            </label>
                            <p className="text-sm text-gray-900">
                              {selectedUser.profileDetails.experience} years
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Activity & Security */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Activity & Security
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Last Login
                      </label>
                      <p className="text-sm text-gray-900">
                        {selectedUser.lastLogin
                          ? new Date(selectedUser.lastLogin).toLocaleString()
                          : "Never logged in"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Login Attempts
                      </label>
                      <p className="text-sm text-gray-900">
                        {selectedUser.loginAttempts}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Account Status
                      </label>
                      <div className="flex items-center space-x-2">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                            selectedUser.status
                          )}`}
                        >
                          {selectedUser.status}
                        </span>
                        {selectedUser.isLocked && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Account Locked
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* History */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Recent History
                  </h3>

                  {/* Status History */}
                  {selectedUser.statusHistory &&
                    selectedUser.statusHistory.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                          Status Changes
                        </h4>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                          {selectedUser.statusHistory
                            .slice(0, 5)
                            .map((entry, index) => (
                              <div
                                key={index}
                                className="text-xs text-gray-600"
                              >
                                <span className="font-medium">
                                  {entry.status}
                                </span>{" "}
                                on{" "}
                                {new Date(entry.changedAt).toLocaleDateString()}
                                {entry.reason && (
                                  <span className="text-gray-500">
                                    {" "}
                                    - {entry.reason}
                                  </span>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                  {/* Role History */}
                  {selectedUser.roleHistory &&
                    selectedUser.roleHistory.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                          Role Changes
                        </h4>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                          {selectedUser.roleHistory
                            .slice(0, 3)
                            .map((entry, index) => (
                              <div
                                key={index}
                                className="text-xs text-gray-600"
                              >
                                <span className="font-medium">
                                  {entry.oldRoles.join(", ")} →{" "}
                                  {entry.newRoles.join(", ")}
                                </span>{" "}
                                on{" "}
                                {new Date(entry.changedAt).toLocaleDateString()}
                                {entry.reason && (
                                  <span className="text-gray-500">
                                    {" "}
                                    - {entry.reason}
                                  </span>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                  {/* Transfer History */}
                  {selectedUser.transferHistory &&
                    selectedUser.transferHistory.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                          Branch Transfers
                        </h4>
                        <div className="max-h-32 overflow-y-auto space-y-1">
                          {selectedUser.transferHistory
                            .slice(0, 3)
                            .map((entry, index) => (
                              <div
                                key={index}
                                className="text-xs text-gray-600"
                              >
                                <span className="font-medium">Transferred</span>{" "}
                                on{" "}
                                {new Date(
                                  entry.transferredAt
                                ).toLocaleDateString()}
                                {entry.reason && (
                                  <span className="text-gray-500">
                                    {" "}
                                    - {entry.reason}
                                  </span>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                  {(!selectedUser.statusHistory ||
                    selectedUser.statusHistory.length === 0) &&
                    (!selectedUser.roleHistory ||
                      selectedUser.roleHistory.length === 0) &&
                    (!selectedUser.transferHistory ||
                      selectedUser.transferHistory.length === 0) && (
                      <p className="text-sm text-gray-500">
                        No recent activity
                      </p>
                    )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t bg-gray-50">
              <div className="flex justify-end">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Branch Assignment Modal */}
      {showBranchModal && selectedUser && (
        <BranchAssignment
          user={selectedUser}
          onClose={() => {
            setShowBranchModal(false);
            setSelectedUser(null);
            fetchUsers();
          }}
        />
      )}
    </div>
  );
};

export default UserManagement;
